import * as React from 'react'
import { create } from '@bufbuild/protobuf'
import { Skeleton } from 'skullet/react'
import {
  ChevronRight,
  File as FileIcon,
  FolderClosed,
  Library,
  Loader2,
} from 'lucide-react'

import {
  AttachmentFolderSchema,
  AttachmentLiteSchema,
  type AttachmentLite,
} from '@/gen/cortado/v1/attachments_pb'
import { useAttachmentFolders, useLibraryAttachments } from '@/stores/attachment-library.store'
import { buildFolderTree, type FolderTreeNode } from './folder-tree-utils'
import { cn } from '@/lib/utils'
import { collapseChevronTransitionClass } from '@/lib/animation-variants'
import { AnimatedCollapse } from '@/components/shared/animated-collapse'
import { formatBytes } from '@/lib/file-utils'
import { EMAIL_ATTACHMENT_LIMITS } from '@/stores/attachments.store'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'

/** Sentinel folder id meaning "all attachments across the whole library". */
const ALL_FOLDERS = '__all__'
/** Sentinel folder id meaning "unfiled attachments at the library root". */
const ROOT_FOLDER = '__root__'

const SAMPLE_FOLDER_TREE: FolderTreeNode[] = buildFolderTree([
  create(AttachmentFolderSchema, { id: 'sample-contracts', name: 'Contracts' }),
  create(AttachmentFolderSchema, { id: 'sample-menus', name: 'Menus and pricing' }),
])

const SAMPLE_ATTACHMENTS: AttachmentLite[] = [
  { filename: 'Catering contract 2026.pdf', sizeBytes: 248_000n },
  { filename: 'Seasonal tasting menu.pdf', sizeBytes: 1_120_000n },
  { filename: 'Venue floor plan.png', sizeBytes: 860_000n },
  { filename: 'Bar package pricing.pdf', sizeBytes: 312_000n },
].map((attachment, i) => create(AttachmentLiteSchema, { id: `sample-${i}`, ...attachment }))

interface AttachmentLibraryPickerProps {
  organizationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Library attachments already selected by the caller. Their ids are
   * pre-checked when the dialog opens and feed the combined size meter.
   */
  initialSelected?: AttachmentLite[]
  /** Files already staged on the caller (uploads) that count toward the cap. */
  existingCount?: number
  /** Bytes already staged on the caller (uploads) that count toward the cap. */
  existingBytes?: number
  /** Invoked with the chosen library attachments when the user confirms. */
  onConfirm: (selected: AttachmentLite[]) => void
}

/**
 * AttachmentLibraryPicker - dialog for choosing org-library attachments to
 * attach to an email.
 *
 * Layout: a folder tree on the left (root / per-folder / all), a grid of the
 * selected folder's attachments with multi-select checkboxes on the right, and
 * a combined size meter (selected library bytes + bytes already staged by the
 * caller) measured against the 20 MB / 10 file email cap.
 *
 * Rendered as a controlled {@link Dialog} so it is safe to mount inside the
 * compose `Sheet` (the dialog re-establishes its own scroll lock, so the
 * no-Portal-in-Sheet rule does not apply to its contents).
 */
export function AttachmentLibraryPicker({
  open,
  onOpenChange,
  ...bodyProps
}: AttachmentLibraryPickerProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        {/* Mount the body only while open so the library queries don't fire on
            mount (the compose sheet keeps this picker mounted while closed). */}
        {open && <PickerBody onOpenChange={onOpenChange} {...bodyProps} />}
      </DialogContent>
    </Dialog>
  )
}

type PickerBodyProps = Omit<AttachmentLibraryPickerProps, 'open'>

/**
 * Inner picker body. Lives behind the `open` gate so its data hooks
 * ({@link useAttachmentFolders} / {@link useLibraryAttachments}) only run while
 * the dialog is open. Mounting fresh on each open also seeds the selection from
 * the caller without a separate effect.
 */
function PickerBody({
  organizationId,
  onOpenChange,
  initialSelected,
  existingCount = 0,
  existingBytes = 0,
  onConfirm,
}: PickerBodyProps) {
  const { folders, isLoading: foldersLoading } = useAttachmentFolders(organizationId)
  const [activeFolder, setActiveFolder] = React.useState<string>(ALL_FOLDERS)

  // Selected library attachments, keyed by id so we keep the full lite record
  // (we need sizeBytes for the combined cap, not just the id). Seeded once from
  // the caller's current selection when this body mounts (i.e. on open).
  const [selected, setSelected] = React.useState<Map<string, AttachmentLite>>(
    () => new Map((initialSelected ?? []).map((item) => [item.id, item]))
  )

  const folderIdForQuery =
    activeFolder === ALL_FOLDERS ? undefined : activeFolder === ROOT_FOLDER ? null : activeFolder

  const { attachments, isLoading: attachmentsLoading, hasLoaded } = useLibraryAttachments(
    organizationId,
    folderIdForQuery
  )

  const tree = React.useMemo(() => buildFolderTree(folders), [folders])

  const selectedList = React.useMemo(() => Array.from(selected.values()), [selected])

  const selectedBytes = React.useMemo(
    () => selectedList.reduce((sum, a) => sum + Number(a.sizeBytes), 0),
    [selectedList]
  )

  const combinedCount = existingCount + selected.size
  const combinedBytes = existingBytes + selectedBytes
  const overFileLimit = combinedCount > EMAIL_ATTACHMENT_LIMITS.MAX_FILES
  const overSizeLimit = combinedBytes > EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES
  const overLimit = overFileLimit || overSizeLimit
  const meterPercent = Math.min(
    100,
    (combinedBytes / EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES) * 100
  )

  const toggle = React.useCallback((attachment: AttachmentLite) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(attachment.id)) {
        next.delete(attachment.id)
      } else {
        next.set(attachment.id, attachment)
      }
      return next
    })
  }, [])

  const handleConfirm = () => {
    onConfirm(selectedList)
    onOpenChange(false)
  }

  const showEmptyAttachments = hasLoaded && !attachmentsLoading && attachments.length === 0

  return (
    <>
        <DialogHeader className="border-b p-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Library className="h-5 w-5" />
            Attachment library
          </DialogTitle>
          <DialogDescription>
            Choose files from your organization&apos;s library to attach to this email.
          </DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 grid-cols-[14rem_1fr] divide-x">
          {/* Folder tree */}
          <div className="min-h-0">
            <ScrollArea className="h-full max-h-[50dvh]">
              <nav aria-label="Library folders" className="flex flex-col gap-0.5 p-2">
                <FolderRow
                  label="All attachments"
                  icon={<Library className="h-4 w-4" />}
                  active={activeFolder === ALL_FOLDERS}
                  onSelect={() => setActiveFolder(ALL_FOLDERS)}
                  depth={0}
                />
                <FolderRow
                  label="Unfiled"
                  icon={<FolderClosed className="h-4 w-4" />}
                  active={activeFolder === ROOT_FOLDER}
                  onSelect={() => setActiveFolder(ROOT_FOLDER)}
                  depth={0}
                />
                <Skeleton
                  data={foldersLoading ? null : tree}
                  sample={SAMPLE_FOLDER_TREE}
                  label="Loading folders"
                >
                  {(tree) => (
                    <div className="flex flex-col gap-0.5">
                      {tree.map((node) => (
                        <FolderTreeBranch
                          key={node.folder.id}
                          node={node}
                          depth={0}
                          activeFolder={activeFolder}
                          onSelect={setActiveFolder}
                        />
                      ))}
                    </div>
                  )}
                </Skeleton>
              </nav>
            </ScrollArea>
          </div>

          {/* Attachment grid */}
          <div className="min-h-0">
            <ScrollArea className="h-full max-h-[50dvh]">
              <div className="p-4">
                <Skeleton
                  data={attachmentsLoading && !hasLoaded ? null : attachments}
                  sample={SAMPLE_ATTACHMENTS}
                  label="Loading attachments"
                  empty={showEmptyAttachments}
                  emptyMessage={
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                      <FolderClosed className="h-8 w-8 text-muted-foreground" />
                      <p className="text-sm font-medium">No attachments here</p>
                      <p className="text-xs text-muted-foreground">
                        Upload files from Settings &rarr; Attachment Library.
                      </p>
                    </div>
                  }
                >
                  {(attachments) => (
                    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {attachments.map((attachment) => {
                        const isSelected = selected.has(attachment.id)
                        const labelId = `lib-att-${attachment.id}`
                        return (
                          <li key={attachment.id}>
                            <label
                              htmlFor={labelId}
                              className={cn(
                                'flex cursor-pointer items-center gap-3 rounded-md border p-3 text-left transition-colors',
                                'hover:bg-accent focus-within:ring-2 focus-within:ring-ring',
                                isSelected && 'border-primary bg-primary/5'
                              )}
                            >
                              <Checkbox
                                id={labelId}
                                checked={isSelected}
                                onCheckedChange={() => toggle(attachment)}
                                aria-label={`Select ${attachment.filename}`}
                              />
                              <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium">
                                  {attachment.filename}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {formatBytes(attachment.sizeBytes)}
                                </p>
                              </div>
                            </label>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </Skeleton>
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Size meter + footer */}
        <div className="border-t p-6 pt-4">
          <div className="mb-4 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {combinedCount} of {EMAIL_ATTACHMENT_LIMITS.MAX_FILES} files
              </span>
              <span
                className={cn(
                  'tabular-nums',
                  overLimit ? 'font-medium text-destructive' : 'text-muted-foreground'
                )}
              >
                {formatBytes(combinedBytes)} /{' '}
                {formatBytes(EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES)}
              </span>
            </div>
            <Progress
              value={meterPercent}
              className={cn(overLimit && '[&_[data-slot=progress-indicator]]:bg-destructive')}
              aria-label="Combined attachment size"
            />
            {overLimit && (
              <p className="text-xs text-destructive" role="alert">
                {overFileLimit
                  ? `Too many files — max ${EMAIL_ATTACHMENT_LIMITS.MAX_FILES} per email.`
                  : `Selection exceeds the ${formatBytes(EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES)} email limit.`}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirm} disabled={overLimit}>
              {attachmentsLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {selected.size > 0
                ? `Attach ${selected.size} ${selected.size === 1 ? 'file' : 'files'}`
                : 'Attach'}
            </Button>
          </DialogFooter>
        </div>
    </>
  )
}

interface FolderRowProps {
  label: string
  icon: React.ReactNode
  active: boolean
  onSelect: () => void
  depth: number
  /**
   * Disclosure state for parent folders. When `expanded` is a boolean a real
   * sibling toggle button is rendered; when undefined a spacer keeps alignment.
   * The toggle is a sibling (not nested) so the row stays a valid single button.
   */
  expanded?: boolean
  onToggle?: () => void
}

/** A single selectable row in the folder tree, with an optional sibling toggle. */
function FolderRow({ label, icon, active, onSelect, depth, expanded, onToggle }: FolderRowProps) {
  return (
    <div
      className={cn(
        'flex items-center rounded-md transition-colors',
        active ? 'bg-accent' : 'hover:bg-accent'
      )}
      style={{ paddingLeft: `${depth * 0.75 + 0.25}rem` }}
    >
      {onToggle ? (
        <button
          type="button"
          aria-label={expanded ? 'Collapse folder' : 'Expand folder'}
          aria-expanded={expanded}
          onClick={onToggle}
          className="flex h-6 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronRight
            className={cn('h-3.5 w-3.5', collapseChevronTransitionClass, expanded && 'rotate-90')}
          />
        </button>
      ) : (
        <span className="w-5 shrink-0" />
      )}
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-1.5 rounded-md py-1.5 pr-2 text-left text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active && 'font-medium'
        )}
      >
        <span className="shrink-0 text-muted-foreground">{icon}</span>
        <span className="truncate">{label}</span>
      </button>
    </div>
  )
}

interface FolderTreeBranchProps {
  node: FolderTreeNode
  depth: number
  activeFolder: string
  onSelect: (folderId: string) => void
}

/** Recursive branch of the folder tree with expand/collapse for children. */
function FolderTreeBranch({ node, depth, activeFolder, onSelect }: FolderTreeBranchProps) {
  const [expanded, setExpanded] = React.useState(true)
  const hasChildren = node.children.length > 0

  return (
    <div>
      <FolderRow
        label={node.folder.name}
        icon={<FolderClosed className="h-4 w-4" />}
        active={activeFolder === node.folder.id}
        onSelect={() => onSelect(node.folder.id)}
        depth={depth}
        expanded={hasChildren ? expanded : undefined}
        onToggle={hasChildren ? () => setExpanded((v) => !v) : undefined}
      />
      {hasChildren && (
        <AnimatedCollapse open={expanded}>
          {node.children.map((child) => (
            <FolderTreeBranch
              key={child.folder.id}
              node={child}
              depth={depth + 1}
              activeFolder={activeFolder}
              onSelect={onSelect}
            />
          ))}
        </AnimatedCollapse>
      )}
    </div>
  )
}
